'use client';

import { useRouter } from 'next/navigation';
import { useState, useEffect } from 'react';
import { getUserData, logout, setUserData, setLineData, getMainPage, setMainPage } from '@/lib/auth';
import { Link, LogOut, Lock, ClipboardList, KeyRound, Info, ChevronRight } from 'lucide-react';
import { CustomerModel, LineLiffModel, PointExpireCustomerModel } from '../../models'
import { useSearchParams } from 'next/navigation';
import { numberFunc, dateFunc, colorFunc } from '../../utils'

import ConfirmModal from './ConfirmModal';
import TierModal from './TierModal';
import HistoryPage from './HistoryPage';
import TierPage from './TierPage';
import RePassword from './RePassword';

const point_expire_customer_model = new PointExpireCustomerModel()
const customer_model = new CustomerModel()
const line_model = new LineLiffModel()

export default function ProfilePage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const page = searchParams.get('page');
  const name = searchParams.get('name');
  const picture = searchParams.get('picture');
  const customer_line_id = searchParams.get('customer_line_id');

  const main_page = getMainPage();
  const [color, setColor] = useState<any>('');
  const [user, setUser] = useState<any>([]);
  const [point_expire_customer, setPointExpireCustomer] = useState<any>([]);
  const [customer_tier_type, setCustomerTierType] = useState<any>([]);
  const [customer_tier_list, setCustomerTierList] = useState<any>([]);
  const [line_setting, setLineSetting] = useState<any>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tierModal, setTierModal] = useState(false);
  const [percentage, setPercentage] = useState<any>(0);
  
  useEffect(() => {
    (async () => {
      try {
        const userData = await getUserData();
        const line_settings = await line_model.getLineLiffBy({})

        if (line_settings.data && line_settings.data.length) setLineSetting(line_settings.data[0]);
        if (userData) {
          if(!userData.customer_line_id && customer_line_id){
            await updateCustomerLineBy(userData)
          }else{
            if(userData.customer_tier_lists){
              const percent = numberFunc.toInt((numberFunc.toFloat(userData.customer_tier_balance) / (numberFunc.toFloat(userData.customer_tier_lists.customer_tier_list_price) + 1)) * 100)
              setPercentage(percent);
            }

            setUser(userData);
            setPointExpireCustomer(userData.point_expire_customers);
            setCustomerTierType(userData.customer_tier_type);
            setColor(userData.customer_tier_type ? userData.customer_tier_type.customer_tier_type_color : '');
            setCustomerTierList(userData.customer_tier_lists);
          }
          const point_expire_customers = await point_expire_customer_model.getPointExpireCustomerBy({ customer_code: userData.customer_code, next_expire: 1 })
          if (point_expire_customers.data && point_expire_customers.data.length) setPointExpireCustomer(point_expire_customers.data[0]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    })()
  }, []);

  const updateCustomerLineBy = async (userData: any) => {
    if(!userData.customer_line_id && customer_line_id){
      await customer_model.updateCustomerLineBy({ 
        customer_code: userData.customer_code, 
        customer_line_id,
        customer_line_name: name,
        customer_line_picture: picture 
      })
      userData.customer_line_id = customer_line_id
      userData.customer_line_picture = picture
      userData.customer_line_name = name
      setUser(userData);
      setUserData(userData);
      setLineData({
        customer_code: userData.customer_code,
        customer_line_id: customer_line_id || '',
        picture: picture || '',
        name: name || '',
      });
      
      router.push(`/?tab=${encodeURIComponent('profile')}`);
    }
  };

  const handleLogout = () => {
    if (confirm('คุณต้องการออกจากระบบหรือไม่?')) {
      logout();
      setMainPage('');
      main_page === 'main' ? router.push('/login?main_page=1') : router.push('/login');
    }
  };

  const refresUser = async () => {
    const customer = await customer_model.getCustomerByCode({ customer_code: user.customer_code, })

    if (customer) {
      if(customer.customer_tier_lists){
        const percent = numberFunc.toInt((numberFunc.toFloat(customer.customer_tier_balance) / (numberFunc.toFloat(customer.customer_tier_lists.customer_tier_list_price) + 1)) * 100)
        setPercentage(percent);
      }

      setUser(customer);
      setUserData(customer);
      setPointExpireCustomer(customer.point_expire_customers);
      setCustomerTierType(customer.customer_tier_type);
      setColor(customer.customer_tier_type.customer_tier_type_color);
      setCustomerTierList(customer.customer_tier_lists);
    }
  };
  
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refresUser();
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  };

  const handleLoginLINE = () => {
    const client_id = process.env.NEXT_PUBLIC_LINE_CLIENT_ID;
    const redirect_uri = encodeURIComponent(`${window.location.origin}/api/auth/line/link`);
    const state = Math.random().toString(36).substring(7);
    const scope = 'profile openid email';
    const lineLoginUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${client_id}&redirect_uri=${redirect_uri}&state=${state}&scope=${scope}`;
    window.location.href = lineLoginUrl;
  };

  const unlinkLINE = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    await customer_model.updateCustomerLineBy({ 
      customer_code: user.customer_code, 
      customer_line_id: '',
      customer_line_name: '',
      customer_line_picture: '' 
    })
    user.customer_line_id = ''
    setUser(user);
    setUserData(user);
    setIsModalOpen(false);
    router.push(`/?tab=${encodeURIComponent('profile')}`);
  };

  const routeHistory = () => {
    router.push(`/?tab=${encodeURIComponent('profile')}&page=${encodeURIComponent('history')}`);
  };

  const routeRePassword = () => {
    router.push(`/?tab=${encodeURIComponent('profile')}&page=${encodeURIComponent('repassword')}`);
  };

  const routeTier = () => {
    router.push(`/?tab=${encodeURIComponent('profile')}&page=${encodeURIComponent('tier')}`);
  };

  const viaColor = color ? colorFunc(String(color), -21) : undefined;

  return (
    <div className="pb-20 bg-orange-100 min-h-screen">
      {!page ? <>
        <div className="relative h-65 bg-gradient-to-r from-green-400 to-orange-400 overflow-hidden">
          <div className="relative h-full p-3 flex flex-col">
            <div 
              className={`bg-gradient-to-b from-[${color}] via-gray-600 to-slate-900 rounded-2xl p-6 shadow-2xl flex-1`}
              style={{
                background: `linear-gradient(180deg,${color},${viaColor},${color})`,
                transition: 'width 800ms ease',
              }}
            >
              <div className="flex items-start justify-between mb-6">
                <div className={`text-3xl font-bold text-gray-300`}>
                  {!line_setting.line_liff_tier && customer_tier_type ? customer_tier_type.customer_tier_type_name : ''}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-transform transform disabled:opacity-60 ${isRefreshing ? 'bg-gray-500 scale-110' : 'bg-gray-700 hover:scale-95'}`}
                    onClick={() => handleRefresh()}
                    disabled={isRefreshing}
                    aria-label="refresh"
                  >
                    <svg className={`w-5 h-5 text-white ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
    
                  <button className={`w-9 h-9 rounded-full flex items-center justify-center bg-gray-700 hover:scale-95`}  onClick={() => setTierModal(true)}>
                    <Info className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
              
              <div className="pt-10">
                <div className="space-y-2 mb-4">
                  <div className={`flex items-center gap-2 ${point_expire_customer && point_expire_customer.customer_code ? '' : 'pt-8'}`}>
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-green-600 font-bold text-lg">P</div>
                    <div className="flex-1">
                      <div>
                        <span className="text-3xl font-semibold text-white mr-1">{numberFunc.decimalFix(user.points_balance, 0)}</span>
                        <span className="text-sm font-black text-white/80 mr-2">เเต้ม</span>
                        <span className="inline-flex items-center rounded-md bg-red-400/20 px-2 py-1 text-sm text-red-400 inset-ring inset-ring-red-300/30">
                          <Lock className="w-4 h-4" /> <label className='ml-1 mt-1 text-red-400'>ล็อค</label>
                        </span>
                      </div>
                    </div>
                  </div>
                  {point_expire_customer && point_expire_customer.customer_code &&
                    <div className="flex items-center">
                      <div className="flex-1">
                        <div>
                          <span className="text-xs text-white/80 mr-1">หมดอายุ</span>
                          <span className="text-xs font-semibold text-white mr-10">{dateFunc.toFormat(point_expire_customer.point_expire_customer_expire_date, 'DD-MM-YYYY')}</span>
                          <span className="text-xs font-semibold text-white mr-1">{numberFunc.decimalFix(point_expire_customer.point_expire_customer_left, 0)}</span>
                          <span className="text-xs text-white/80">คะแนน</span>
                        </div>
                      </div>
                    </div>
                  }
                </div>
                
                <div className="bg-gray/50 rounded-full">
                  <div className="h-4 bg-gray-100 rounded-full overflow-hidden relative">
                    <div
                      className="absolute left-0 top-0 bottom-0 rounded-full"
                      aria-hidden="true"
                      style={{
                        width: `${Math.max(1, Math.min(100, percentage))}%`,
                        background: 'linear-gradient(90deg,#16a34a,#14b8a6,#10b981)',
                        transition: 'width 800ms ease',
                      }}
                    />
                    <div className="relative h-full flex items-center justify-center">
                      <span className="text-xs font-semibold">{percentage}%</span>
                    </div>
                  </div>
                </div>
                <div className="flex pt-2 text-xs text-white/90 min-h-6" onClick={routeTier}>
                  {!line_setting.line_tier_progress && customer_tier_list ? 
                    <>สะสมอีกยอดซื้อ {numberFunc.decimalFix(((numberFunc.toInt(customer_tier_list.customer_tier_list_price) + 1) - user.customer_tier_balance), 0)} บาท เพื่อเลื่อนระดับต่อไป <ChevronRight className="ml-1 w-4 h-4"/></> 
                  : ''}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">ข้อมูลส่วนตัว</h3>
          </div>
          <div className="space-y-4">
            <div>
              <div className="text-sm font-bold mb-1">ชื่อ - นามสกุล</div>
              <div className="text-gray-600">
                {`${user.customer_full_name} `}
                {user.customer_line_name ? `(${user.customer_line_name})` : ''}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-bold mb-1">เบอร์มือถือ</div>
                <div className="text-gray-600">{user.customer_tel}</div>
              </div>
              {!line_setting.line_liff_email && user.customer_email ?
                <div>
                  <div className="text-sm font-bold mb-1">อีเมล</div>
                  <div className="text-gray-600">{user.customer_email}</div>
                </div>
              : ''}
            </div>

            {!line_setting.line_liff_address ?
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="text-sm font-bold mb-1">ที่อยู่</div>
                  <div className="text-gray-600 text-sm">{user.customer_address}</div>
                </div>
              </div>
            : ''}

            <div className="h-1 bg-orange-500 rounded"></div>

            <button className="w-full bg-blue-500 hover:bg-blue-600 py-3 text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2 shadow-lg" onClick={routeHistory}>
              <ClipboardList className="w-5 h-5" />ประวัติการซื้อ
            </button>
            
            <button className="w-full bg-yellow-500 hover:bg-yellow-600 py-3 text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2 shadow-lg" onClick={routeRePassword}>
              <KeyRound className="w-5 h-5" />เปลี่ยนรหัสผ่าน
            </button>
            
            {main_page === 'main' ? user.customer_line_id ? 
              <button className="w-full bg-green-200 hover:bg-green-300 py-3 text-black/60 rounded-lg font-bold transition-all flex items-center justify-center gap-2" onClick={unlinkLINE}>
                <Link className="w-5 h-5" />เชื่อมต่อบัญชี LINE เเล้ว
              </button>
            :
              <button className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-bold transition-all" onClick={handleLoginLINE}>
                เชื่อมต่อบัญชี LINE
              </button>
            : null}
            <button onClick={handleLogout} className="w-full bg-rose-600 hover:bg-red-600 text-white py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2 shadow-lg">
              <LogOut className="w-5 h-5" />ออกจากระบบ
            </button>
          </div>
        </div>

        <ConfirmModal
          isOpen={isModalOpen}
          message="ต้องยกเลิกการเชื่อมต่อ LINE?"
          onConfirm={handleConfirm}
          onCancel={() => setIsModalOpen(false)}
        />
        <TierModal
          isOpen={tierModal}
          tier={customer_tier_type}
          benefits={customer_tier_type && customer_tier_type.customer_tier_benefits}
          onCancel={() => setTierModal(false)}
        />
      </> : page === 'history' ? <HistoryPage /> : page === 'tier' ? <TierPage /> : <RePassword/>}
    </div>
  );
}
